import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  Checkbox,
  Grid,
  List,
  ListItemText,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
  createFilterOptions,
  debounce,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CenteredGridItem } from "../../../common/components/functional/FilterSearchField/styled";
import { SelectAutocompleteMulti } from "../../../common/components/functional/SelectAutocomplete/SelectAutocompleteMulti";
import {
  RoundedButton,
  SelectLight,
} from "../../../common/components/styled/styled";
import { compareByFieldFi } from "../../../common/helpers/common";
import { CustomerOrgCompany } from "../../../common/types/apiTypes";
import { getCoreUserFullName } from "../../../common/utils/users";
import {
  AutocompleteCustomerCC,
  customerCostCenterAutocomplete,
  customerOrgAndCompAutocomplete,
} from "../../customer/customerAPI";
import {
  fetchExpertUsers,
  selectAllExpertUsers,
} from "../../user/expertUsersSlice";
import { CoreUser } from "../../user/types/entities";
import {
  INVOICE_BILLING_TYPES,
  INVOICE_STATUS_TYPES,
  INVOICE_TYPES,
  INVOICE_WAVE_ONE,
  INVOICE_WAVE_TWO,
} from "../constants";
import { EezyCompany } from "../invoiceAPI";
import { fetchEezyCompanies } from "../invoiceSlice";
import { InvoiceFilterFormTypes } from "./common";
import { FilterRow } from "./FilterRow";

interface Props {
  sendFilters: () => void;
}

type CustomerOrgType = {
  org: string;
  company: string;
  orgId: string;
};

const FiltersForm = ({ sendFilters }: Props) => {
  const { t } = useTranslation("invoices");
  const dispatch = useAppDispatch();
  const { control, watch } = useFormContext<InvoiceFilterFormTypes>();
  const customerOrgName = watch("customerOrganizationNames");
  const customerCC = watch("customerCC");
  const expertUsers = useAppSelector((state) => selectAllExpertUsers(state));

  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>(
    [],
  );

  const [customerSearchString, setCustomerSearchString] =
    useState(customerOrgName);
  const [customerOrganizationOptions, setCustomerOrganizationOptions] =
    useState<CustomerOrgCompany[]>([]);
  const [customerOrg, setCustomerOrg] = useState<CustomerOrgType | null>(null);
  const [custCCSearchString, setCustCCSearchString] = useState(
    customerCC?.name || "",
  );
  const [custCCOptions, setCustCCOptions] = useState<AutocompleteCustomerCC[]>(
    [],
  );

  const handleCustomerSearchStringChange = debounce(
    (value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setCustomerSearchString(value);
      }
    },
    500,
  );

  const handleCustomerCCSearchStringChange = debounce(
    (value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setCustCCSearchString(value);
      }
    },
    500,
  );

  const dispatchCustomerOrgSearch = (searchString: string) => {
    try {
      customerOrgAndCompAutocomplete({ name: searchString }).then((res) => {
        setCustomerOrganizationOptions(res);
      });
    } catch (e: any) {
      console.error("Failed to fetch CustomerOrganizations: ", e);
    }
  };

  const dispatchCustomerCCSearch = useCallback(
    (searchString: string) => {
      try {
        customerCostCenterAutocomplete({
          search: searchString,
          ...(customerOrg && { customerOrgId: customerOrg?.orgId }),
        }).then((res) => {
          setCustCCOptions(res);
        });
      } catch (e: any) {
        console.error("Failed to fetch CustomerCCs: ", e);
      }
    },
    [customerOrg],
  );

  const customerFilterOptions = useMemo(() => {
    return customerOrganizationOptions.flatMap((item) =>
      item.customerOrganizations.map((org) => ({
        org: org.name,
        company: item.name,
        orgId: org.id,
      })),
    );
  }, [customerOrganizationOptions]);

  const expertUserOptions = useMemo(() => {
    return expertUsers
      .map((user: CoreUser) => ({
        id: user.id,
        name: getCoreUserFullName(user),
      }))
      .sort(compareByFieldFi((user) => user.name));
  }, [expertUsers]);

  // Option names are saved to the filter chip's lookupValues, so they are translated here
  const billingTypeOptions = useMemo(
    () =>
      INVOICE_BILLING_TYPES.map((billingType) => ({
        id: billingType,
        name: t(billingType),
      })),
    [t],
  );

  const getOrg = useCallback(
    (orgName: string) => {
      if (!orgName) return null;
      return customerFilterOptions.find((org) => org.org === orgName) || null;
    },
    [customerFilterOptions],
  );

  useEffect(() => {
    const org = getOrg(customerOrgName);
    setCustomerOrg(org);
  }, [customerOrgName, getOrg]);

  useEffect(() => {
    dispatch(fetchEezyCompanies({})).then(
      (action) => isFulfilled(action) && setEezyCompanyOptions(action.payload),
    );
    dispatch(fetchExpertUsers({}));
  }, [dispatch]);

  useEffect(() => {
    if (customerSearchString !== "") {
      dispatchCustomerOrgSearch(customerSearchString);
    }
  }, [customerSearchString]);

  useEffect(() => {
    if (custCCSearchString !== "") {
      dispatchCustomerCCSearch(custCCSearchString);
    }
  }, [custCCSearchString, dispatchCustomerCCSearch]);

  useEffect(() => {
    setCustCCSearchString(customerCC?.name || "");
  }, [customerCC]);

  return (
    <Box sx={{ marginTop: "20px" }}>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <FilterRow label={t("invoiceTypes")}>
            <Controller
              name="invoiceTypes"
              control={control}
              render={({ field }) => (
                <SelectLight
                  {...field}
                  value={field.value}
                  label={null}
                  fullWidth
                >
                  {INVOICE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(type)}
                    </MenuItem>
                  ))}
                </SelectLight>
              )}
            />
          </FilterRow>
          <FilterRow label={t("date")}>
            <Controller
              name="invoiceDateFrom"
              control={control}
              render={({ field }) => (
                <DesktopDatePicker
                  {...field}
                  slotProps={{ textField: { size: "small" } }}
                />
              )}
            />
            <>{t("separator")}</>
            <Controller
              name="invoiceDateTo"
              control={control}
              render={({ field }) => (
                <DesktopDatePicker
                  {...field}
                  slotProps={{ textField: { size: "small" } }}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("dueDate")}>
            <Controller
              name="invoiceDueDateFrom"
              control={control}
              render={({ field }) => (
                <DesktopDatePicker
                  {...field}
                  slotProps={{ textField: { size: "small" } }}
                />
              )}
            />
            <>{t("separator")}</>
            <Controller
              name="invoiceDueDateTo"
              control={control}
              render={({ field }) => (
                <DesktopDatePicker
                  {...field}
                  slotProps={{ textField: { size: "small" } }}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("valueDate")}>
            <Controller
              name="invoiceValueDateFrom"
              control={control}
              render={({ field }) => (
                <DesktopDatePicker
                  {...field}
                  slotProps={{ textField: { size: "small" } }}
                />
              )}
            />
            <>{t("separator")}</>
            <Controller
              name="invoiceValueDateTo"
              control={control}
              render={({ field }) => (
                <DesktopDatePicker
                  {...field}
                  slotProps={{ textField: { size: "small" } }}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("status")}>
            <Controller
              name="invoiceStatuses"
              control={control}
              render={({ field }) => (
                <SelectLight
                  {...field}
                  multiple
                  fullWidth
                  value={field.value}
                  label={null}
                  renderValue={() =>
                    field.value
                      .flatMap((status: string) => t(status))
                      .join(", ")
                  }
                >
                  {INVOICE_STATUS_TYPES.map((statusType) => (
                    <MenuItem key={statusType} value={statusType} dense>
                      <Checkbox checked={field.value.includes(statusType)} />
                      <ListItemText primary={t(statusType)} />
                    </MenuItem>
                  ))}
                </SelectLight>
              )}
            />
          </FilterRow>
          <FilterRow label={t("invoiceWaves")}>
            <Controller
              name="invoiceWaves"
              control={control}
              render={({ field }) => (
                <SelectLight
                  {...field}
                  value={field.value}
                  label={null}
                  fullWidth
                >
                  {[INVOICE_WAVE_ONE, INVOICE_WAVE_TWO].map((wave) => (
                    <MenuItem key={wave} value={wave}>
                      {t(`${wave}`)}
                    </MenuItem>
                  ))}
                </SelectLight>
              )}
            />
          </FilterRow>
          <FilterRow label={t("billingType")}>
            <Controller
              control={control}
              name="billingTypes"
              render={({ field }) => (
                <SelectAutocompleteMulti
                  value={field.value || []}
                  options={billingTypeOptions}
                  onChange={(newValue) => field.onChange(newValue || [])}
                />
              )}
            />
          </FilterRow>
          <CenteredGridItem item container spacing={2} xs={12}>
            <Grid item xs={12}>
              <RoundedButton variant="contained" onClick={sendFilters}>
                {t("getInvoices")}
              </RoundedButton>
            </Grid>
          </CenteredGridItem>
        </Grid>
        <Grid item xs={6}>
          <FilterRow label={t("customerOrganizationName")}>
            <Controller
              name="customerOrganizationNames"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  fullWidth
                  value={customerOrg}
                  isOptionEqualToValue={(option, selectedValue) =>
                    option.org === selectedValue.org
                  }
                  getOptionLabel={(option) => option.org}
                  onChange={(_, newValue) =>
                    onChange(newValue ? newValue.org : "")
                  }
                  options={customerFilterOptions}
                  filterOptions={createFilterOptions({
                    stringify: (option) => JSON.stringify(option),
                  })}
                  groupBy={(option: any) => option.company}
                  noOptionsText={t("common:writeToSearch")}
                  renderGroup={({ group, children }) => (
                    <>
                      <ListSubheader
                        disableSticky
                        sx={{
                          color: "#9167CA",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          lineHeight: "20px",
                        }}
                      >
                        <Typography variant="header">{group}</Typography>
                      </ListSubheader>
                      <List sx={{ paddingLeft: "32px" }}>{children}</List>
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value={customerSearchString}
                      size="small"
                      name="customerOrgCompany"
                      required
                    />
                  )}
                  onInputChange={(
                    _,
                    newValue: string,
                    reason: AutocompleteInputChangeReason,
                  ) => {
                    handleCustomerSearchStringChange(newValue, reason);
                  }}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("customerCostCenter")}>
            <Controller
              name="customerCC"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  fullWidth
                  value={value}
                  isOptionEqualToValue={(option, selectedValue) =>
                    option.id === selectedValue.id
                  }
                  getOptionLabel={(option) => option.name}
                  onChange={(_, newValue) => onChange(newValue)}
                  options={custCCOptions}
                  filterOptions={createFilterOptions({
                    stringify: (option) => JSON.stringify(option),
                  })}
                  groupBy={(option: any) => option.customerOrgName}
                  renderGroup={({ group, children }) => (
                    <>
                      <ListSubheader
                        disableSticky
                        sx={{
                          color: "#9167CA",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          lineHeight: "20px",
                        }}
                      >
                        <Typography variant="header">{group}</Typography>
                      </ListSubheader>
                      <List sx={{ paddingLeft: "32px" }}>{children}</List>
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value={custCCSearchString}
                      size="small"
                      name="customerCC"
                    />
                  )}
                  onInputChange={(
                    _,
                    newValue: string,
                    reason: AutocompleteInputChangeReason,
                  ) => {
                    handleCustomerCCSearchStringChange(newValue, reason);
                  }}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("eezyCompanyName")}>
            <Controller
              name="eezyCompanyNames"
              control={control}
              render={({ field }) => (
                <SelectLight
                  {...field}
                  multiple
                  fullWidth
                  value={field.value}
                  label={null}
                  renderValue={() =>
                    field.value
                      .flatMap((status: string) => t(status))
                      .join(", ")
                  }
                >
                  {eezyCompanyOptions.map((eezyCompany) => (
                    <MenuItem
                      key={eezyCompany.eezyCompanyId}
                      value={eezyCompany.name}
                      dense
                    >
                      <Checkbox
                        checked={field.value.includes(eezyCompany.name)}
                      />
                      <ListItemText primary={eezyCompany.name} />
                    </MenuItem>
                  ))}
                </SelectLight>
              )}
            />
          </FilterRow>
          <FilterRow label={t("contactPerson")}>
            <Controller
              control={control}
              name="salesPersons"
              render={({ field }) => (
                <SelectAutocompleteMulti
                  value={field.value || []}
                  options={expertUserOptions}
                  onChange={(newValue) => field.onChange(newValue || [])}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("vatExcludedAmount")}>
            <Controller
              control={control}
              name="vatExcludedAmountFrom"
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  size="small"
                  type="number"
                  onChange={onChange}
                />
              )}
            />
            <Typography>{t("separator")}</Typography>
            <Controller
              control={control}
              name="vatExcludedAmountTo"
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  size="small"
                  type="number"
                  onChange={onChange}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("vatAmount")}>
            <Controller
              control={control}
              name="vatAmountFrom"
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  size="small"
                  type="number"
                  onChange={onChange}
                />
              )}
            />
            <Typography>{t("separator")}</Typography>
            <Controller
              control={control}
              name="vatAmountTo"
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  size="small"
                  type="number"
                  onChange={onChange}
                />
              )}
            />
          </FilterRow>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FiltersForm;
